import { useMemo, useRef, useState } from "react"
import {
    DragDropContext,
    Droppable,
    Draggable,
    type DropResult,
    type DragStart,
} from "@hello-pangea/dnd"
import styles from "./Tasks.module.scss"

type Priority = "NONE" | "LOW" | "HIGH"

interface Task {
    id: string
    title: string
    description: string
    priority: Priority
    comments: string[]
    attachments: number
}

interface Lane {
    id: string
    title: string
    taskIds: string[]
}

type LanesState = Record<string, Lane>
type TasksState = Record<string, Task>

const laneColors: Record<string, string> = {
    "lane-1": "#6c757d", // Backlog (grey)
    "lane-2": "#e03131", // ToDo (red)
    "lane-3": "#f08c00", // WIP (orange)
    "lane-4": "#74c0fc", // To release (light blue)
    "lane-5": "#2f9e44", // Done (green)
    "lane-6": "#e03131", // Bugs (red)
    "lane-7": "#2b8a3e", // Bugs Done (dark green)
}

type Seed = {
    title: string
    lane: "Backlog" | "ToDo" | "WIP" | "To release" | "Done" | "Bugs" | "Bugs Done"
    desc?: string
    priority?: Priority
}

const seeds: Seed[] = [
    { lane: "WIP", title: "Wikis", desc: "Wikis with links" },
    {
        lane: "WIP",
        title: "Advanced To-do",
        desc: "To-do (with A. Personal or team membership, B. lanes including Archive at the end & C. infinite hierarchy as before, ie a tree of arbitrary depth sub-tasks in tree view (vs lane view) but otherwise simple initially)",
    },

    { lane: "Backlog", title: "UI Updates", desc: "General: sweep minor UI Updates" },
    {
        lane: "Backlog",
        title: "Truncate Date in sidebar",
        desc: "Truncate month in sidebar dates (Aug 5 | 2024)",
    },
    { lane: "Backlog", title: "Wiki", desc: "General: sweep minor UI Updates" },

    {
        lane: "ToDo",
        title: "File Listing",
        desc: "File listing section should be functional (like inline chat file listing).",
    },
    {
        lane: "ToDo",
        title: "Recent/All Toggle",
        desc: "Add Recent = week / All toggle to file listings.",
    },
    {
        lane: "ToDo",
        title: "Movable Sections",
        desc: "All sections should be minimizable to header & movable within & between columns",
    },

    // Org membership, teams & RAG
    { lane: "ToDo", title: "Org Membership", desc: "Org membership with shared docs & app items" },
    {
        lane: "ToDo",
        title: "Team Membership",
        desc: "Team membership for messaging groups, to-do lists & wikis",
    },
    {
        lane: "ToDo",
        title: "Shared RAG",
        desc: "Shared RAG index per org, retrieval should search both personal & org RAG.",
    },

    { lane: "ToDo", title: "Messaging", desc: "Messaging with teams" },
    {
        lane: "ToDo",
        title: "Email Mock-up",
        desc: "Email mock-up (both messages & email shown in section listing, grouped by type. Both types are toggle-able off in listing.",
    },
    {
        lane: "ToDo",
        title: "Python App",
        desc: "Python ‘app’. LAST PRIORITY. Allow (protected) running of user created python code.",
    },

    { lane: "ToDo", title: "LLM Choice", desc: "LLM choice: GPT / Anthropic / Grok" },
    {
        lane: "ToDo",
        title: "Semantic File Search",
        desc: "Make inline file listing sensetive to semantic search commands (eg list PDFs about climate change). Do it by feeding the LLM the file list. Post-format.",
    },
    {
        lane: "ToDo",
        title: "RAG Filters",
        desc: "Make these apps all Integrate into RAG index with a filter label = messaging / to-do / wikis. Normally off, use these RAG filters if the LLM thinks it should (at retrieval query creation time)",
    },
    {
        lane: "ToDo",
        title: "Inline RAG Items",
        desc: "List app items inline matching filtered RAG retrieval",
    },
    {
        lane: "ToDo",
        title: "LLM Item Creation",
        desc: "Allow the LLM to create app items.  Docs in suggested incl new folder (eg if asked to create a report or essay or table), (draft) messages, to-do lists & items & wikis. List inline with APPROVE button.",
    },
    {
        lane: "ToDo",
        title: "Chat Conversion",
        desc: "Any chat post should be copyable, editable, re-runable & tirnsbke into a doc or wiki page",
    },
    {
        lane: "ToDo",
        title: "Smart References",
        desc: "Allow with auto completion & escape, # to reference to-do list items, @ people, $ wiki pages",
    },
    { lane: "ToDo", title: "Chat Tables", desc: "Improve formatting of assistant chat — Tables" },
    { lane: "ToDo", title: "Chat Code", desc: "Improve formatting of assistant chat — Code" },
    {
        lane: "ToDo",
        title: "Chat Charts",
        desc: "Improve formatting of assistant chat — Charts. Create inline copyable charts via a python chart creator.",
    },
]

const buildInitialState = () => {
    const tasks: TasksState = {}
    const laneByName: Record<Seed["lane"], string> = {
        Backlog: "lane-1",
        ToDo: "lane-2",
        WIP: "lane-3",
        "To release": "lane-4",
        Done: "lane-5",
        Bugs: "lane-6",
        "Bugs Done": "lane-7",
    }
    const laneTaskIds: Record<string, string[]> = {
        "lane-1": [],
        "lane-2": [],
        "lane-3": [],
        "lane-4": [],
        "lane-5": [],
        "lane-6": [],
        "lane-7": [],
    }

    let next = 1
    for (const s of seeds) {
        const id = String(next++)
        tasks[id] = {
            id,
            title: s.title,
            description: s.desc ?? "",
            priority: s.priority ?? "NONE",
            comments: [],
            attachments: 0,
        }
        laneTaskIds[laneByName[s.lane]].push(id)
    }

    const lanes: LanesState = {
        "lane-1": { id: "lane-1", title: "Backlog", taskIds: laneTaskIds["lane-1"] },
        "lane-2": { id: "lane-2", title: "ToDo", taskIds: laneTaskIds["lane-2"] },
        "lane-3": { id: "lane-3", title: "WIP", taskIds: laneTaskIds["lane-3"] },
        "lane-4": { id: "lane-4", title: "To release", taskIds: laneTaskIds["lane-4"] },
        "lane-5": { id: "lane-5", title: "Done", taskIds: laneTaskIds["lane-5"] },
        "lane-6": { id: "lane-6", title: "Bugs", taskIds: laneTaskIds["lane-6"] },
        "lane-7": { id: "lane-7", title: "Bugs Done", taskIds: laneTaskIds["lane-7"] },
    }

    return { tasks, lanes, nextId: next }
}

const { tasks: initialTasks, lanes: initialLanes, nextId: initialNextId } = buildInitialState()

const laneOrder: string[] = ["lane-1", "lane-2", "lane-3", "lane-4", "lane-5", "lane-6", "lane-7"]

/** Helper renderers */
type StylesModule = typeof styles
const priorityLabel = (p: Priority) =>
    p === "HIGH" ? "High Priority" : p === "LOW" ? "Low Priority" : "None"

const priorityClass = (p: Priority, s: StylesModule) =>
    p === "HIGH" ? s.priorityHigh : p === "LOW" ? s.priorityLow : ""

/** drop animation smoothing (kept) */
const getItemStyle = (
    draggableStyle: React.CSSProperties | undefined,
    snapshot: {
        isDragging: boolean
        isDropAnimating: boolean
        dropAnimation?: { duration: number; curve: string }
    }
): React.CSSProperties => {
    if (snapshot.isDropAnimating) {
        return {
            ...draggableStyle,
            transitionDuration: "0.001s",
        }
    }
    return draggableStyle || {}
}

const Tasks: React.FC<{
    user: { name?: string } | any
    setUser: React.Dispatch<React.SetStateAction<any>>
}> = ({ user, setUser }) => {
    const [lanes, setLanes] = useState<LanesState>(initialLanes)
    const [tasks, setTasks] = useState<TasksState>(initialTasks)

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [newTaskLaneId, setNewTaskLaneId] = useState<string | null>(null)

    const [viewTaskId, setViewTaskId] = useState<string | null>(null)
    const [newComment, setNewComment] = useState("")

    const [inviteQueryCreate, setInviteQueryCreate] = useState("")
    const [inviteQueryView, setInviteQueryView] = useState("")

    const [dragging, setDragging] = useState(false)

    const nextId = useRef<number>(initialNextId)

    const handleDragStart = (_start: DragStart) => setDragging(true)
    const handleDragEnd = (result: DropResult) => {
        setDragging(false)
        const { destination, source, draggableId } = result

        if (!destination) return
        if (destination.droppableId === source.droppableId && destination.index === source.index)
            return

        const startLane = lanes[source.droppableId]
        const finishLane = lanes[destination.droppableId]
        if (!startLane || !finishLane) return

        if (startLane.id === finishLane.id) {
            const newTaskIds = Array.from(startLane.taskIds)
            newTaskIds.splice(source.index, 1)
            newTaskIds.splice(destination.index, 0, draggableId)

            setLanes((prev) => ({ ...prev, [startLane.id]: { ...startLane, taskIds: newTaskIds } }))
            return
        }

        const startTaskIds = Array.from(startLane.taskIds)
        startTaskIds.splice(source.index, 1)
        const finishTaskIds = Array.from(finishLane.taskIds)
        finishTaskIds.splice(destination.index, 0, draggableId)

        setLanes((prev) => ({
            ...prev,
            [startLane.id]: { ...startLane, taskIds: startTaskIds },
            [finishLane.id]: { ...finishLane, taskIds: finishTaskIds },
        }))
    }

    const openCreateModal = (laneId: string) => {
        setNewTaskLaneId(laneId)
        setIsCreateOpen(true)
    }
    const closeCreateModal = () => {
        setIsCreateOpen(false)
        setNewTaskLaneId(null)
    }

    const handleAddTask = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!newTaskLaneId) return
        const fd = new FormData(e.currentTarget)
        const title = String(fd.get("title") || "").trim()
        const description = String(fd.get("description") || "").trim()
        const priority = (fd.get("priority") as Priority) || "NONE"
        if (!title) return

        const newIdStr = String(nextId.current)

        const newTask: Task = {
            id: newIdStr,
            title,
            description,
            priority,
            comments: [],
            attachments: 0,
        }

        setTasks((prev) => ({ ...prev, [newIdStr]: newTask }))

        const targetLane = lanes[newTaskLaneId]
        const newTaskIds = [newIdStr, ...targetLane.taskIds]
        setLanes((prev) => ({
            ...prev,
            [newTaskLaneId]: { ...targetLane, taskIds: newTaskIds },
        }))

        nextId.current += 1
        closeCreateModal()
    }

    const openViewTask = (taskId: string) => {
        if (dragging) return
        setViewTaskId(taskId)
        setNewComment("")
    }
    const closeViewModal = () => setViewTaskId(null)

    const addComment = () => {
        if (!viewTaskId) return
        const text = newComment.trim()
        if (!text) return
        setTasks((prev) => ({
            ...prev,
            [viewTaskId]: { ...prev[viewTaskId], comments: [...prev[viewTaskId].comments, text] },
        }))
        setNewComment("")
    }

    const moveTaskFromView = (taskId: string, destLaneId: string) => {
        const currentLaneId = Object.keys(lanes).find((lid) => lanes[lid].taskIds.includes(taskId))
        if (!currentLaneId || !lanes[destLaneId]) return
        if (currentLaneId === destLaneId) return

        const sourceLane = lanes[currentLaneId]
        const destLane = lanes[destLaneId]

        const newSourceIds = sourceLane.taskIds.filter((id) => id !== taskId)
        const newDestIds = [taskId, ...destLane.taskIds]

        setLanes((prev) => ({
            ...prev,
            [currentLaneId]: { ...sourceLane, taskIds: newSourceIds },
            [destLaneId]: { ...destLane, taskIds: newDestIds },
        }))
    }

    const viewTask = useMemo(() => (viewTaskId ? tasks[viewTaskId] : null), [viewTaskId, tasks])

    const userInitial = (user?.name || "?").toString().trim().charAt(0).toUpperCase() || "?"

    return (
        <div className={styles.Tasks}>
            <div className={styles.topBar} />

            <div className={styles.boardShell}>
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <div className={styles.lanesRow}>
                        {laneOrder.map((laneId) => {
                            const lane = lanes[laneId]
                            const laneColor = laneColors[laneId] || "#5b8cff"
                            const laneTasks = lane.taskIds
                                .map((taskId) => tasks[taskId])
                                .filter(Boolean)

                            return (
                                <Droppable droppableId={lane.id} key={lane.id}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`${styles.lane} ${
                                                snapshot.isDraggingOver
                                                    ? styles.laneDraggingOver
                                                    : ""
                                            }`}
                                            style={{ borderTop: `4px solid ${laneColor}` }}>
                                            <div className={styles.laneHeader}>
                                                <h3>{lane.title}</h3>
                                                <span>{laneTasks.length}</span>
                                            </div>

                                            <div className={styles.taskList}>
                                                {laneTasks.map((task, index) => (
                                                    <Draggable
                                                        draggableId={task.id}
                                                        index={index}
                                                        key={task.id}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className={`${styles.taskCard} ${
                                                                    snapshot.isDragging
                                                                        ? styles.taskCardDragging
                                                                        : ""
                                                                }`}
                                                                style={getItemStyle(
                                                                    provided.draggableProps.style,
                                                                    snapshot as any
                                                                )}
                                                                onClick={() =>
                                                                    openViewTask(task.id)
                                                                }>
                                                                {task.priority !== "NONE" && (
                                                                    <div
                                                                        className={`${
                                                                            styles.priorityTag
                                                                        } ${priorityClass(
                                                                            task.priority,
                                                                            styles
                                                                        )}`}>
                                                                        {priorityLabel(
                                                                            task.priority
                                                                        )}
                                                                    </div>
                                                                )}

                                                                <p className={styles.taskTitle}>
                                                                    <span
                                                                        className={
                                                                            styles.taskNumericId
                                                                        }
                                                                        style={{
                                                                            color: laneColor,
                                                                        }}>
                                                                        #{task.id}
                                                                    </span>
                                                                    {task.title}
                                                                </p>

                                                                {task.description && (
                                                                    <div
                                                                        className={styles.taskDesc}>
                                                                        {task.description}
                                                                    </div>
                                                                )}

                                                                <div className={styles.taskMeta}>
                                                                    <div
                                                                        className={
                                                                            styles.assignees
                                                                        }>
                                                                        <div
                                                                            className={
                                                                                styles.avatar
                                                                            }
                                                                            aria-hidden="true">
                                                                            {userInitial}
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.stats}>
                                                                        <span title="Comments">
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="14"
                                                                                height="14"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                strokeWidth="2"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round">
                                                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                                            </svg>
                                                                            {task.comments.length}
                                                                        </span>
                                                                        {task.attachments > 0 && (
                                                                            <span title="Attachments">
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width="14"
                                                                                    height="14"
                                                                                    viewBox="0 0 24 24"
                                                                                    fill="none"
                                                                                    stroke="currentColor"
                                                                                    strokeWidth="2"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round">
                                                                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                                                                </svg>
                                                                                {task.attachments}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>

                                            <button
                                                className={styles.addTaskBtn}
                                                onClick={() => openCreateModal(lane.id)}>
                                                + Add Task
                                            </button>
                                        </div>
                                    )}
                                </Droppable>
                            )
                        })}
                    </div>
                </DragDropContext>
            </div>

            {/* Create Task Modal (unchanged aside from numericId removal) */}
            {isCreateOpen && (
                <div className={styles.modalOverlay} onClick={closeCreateModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h2>Add New Task</h2>
                        <p>
                            in <strong>{lanes[newTaskLaneId!]?.title}</strong> lane
                        </p>

                        <form onSubmit={handleAddTask}>
                            <div className={styles.formGroup}>
                                <label htmlFor="title">Task Title</label>
                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    required
                                    autoFocus
                                    placeholder="Task Name"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    placeholder="Describe the task..."></textarea>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="priority">Priority</label>
                                <select id="priority" name="priority" defaultValue="NONE">
                                    <option value="NONE">None</option>
                                    <option value="LOW">Low Priority</option>
                                    <option value="HIGH">High Priority</option>
                                </select>
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    className={styles.btnCancel}
                                    onClick={closeCreateModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.btnCreate}>
                                    Create Task
                                </button>
                            </div>
                        </form>

                        {/* Invite area (dummy) */}
                        <div className={styles.inviteArea}>
                            <div className={styles.inviteRow}>
                                <input
                                    placeholder="Assign people to this board..."
                                    value={inviteQueryCreate}
                                    onChange={(e) => setInviteQueryCreate(e.target.value)}
                                />
                                <button className={styles.inviteBtn} title="Assign (disabled)">
                                    Assign
                                </button>
                            </div>
                            <div className={styles.searchDropdown}>
                                <div className={styles.emptyState}>No users found</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewTask && (
                <div className={styles.modalOverlay} onClick={closeViewModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.viewHeader}>
                            <h2>Viewing Task</h2>
                            <button className={styles.closeBtn} onClick={closeViewModal}>
                                <i className="material-symbols-rounded">close</i>
                            </button>
                        </div>

                        <div className={styles.viewBody}>
                            <div className={styles.viewRow}>
                                <div className={styles.label}>Title</div>
                                <div className={styles.value}>
                                    <strong>#{viewTask.id}</strong> {viewTask.title}
                                </div>
                            </div>

                            <div className={styles.viewRow}>
                                <div className={styles.label}>Priority</div>
                                <div className={styles.value}>
                                    {priorityLabel(viewTask.priority)}
                                </div>
                            </div>

                            <div className={styles.viewRow}>
                                <div className={styles.label}>Description</div>
                                <div className={styles.value}>{viewTask.description || "—"}</div>
                            </div>

                            {/* Lane switcher (unchanged behavior) */}
                            <div className={styles.viewRow}>
                                <div className={styles.label}>Move to Lane</div>
                                <div className={styles.value}>
                                    <select
                                        value={
                                            Object.keys(lanes).find((lid) =>
                                                lanes[lid].taskIds.includes(viewTask.id)
                                            ) || ""
                                        }
                                        onChange={(e) =>
                                            moveTaskFromView(viewTask.id, e.target.value)
                                        }>
                                        {laneOrder.map((lid) => (
                                            <option key={lid} value={lid}>
                                                {lanes[lid].title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className={styles.comments}>
                                <div className={styles.label} style={{ marginBottom: 6 }}>
                                    Comments
                                </div>

                                <div className={styles.commentList}>
                                    {viewTask.comments.length === 0 ? (
                                        <div className={styles.empty}>No comments yet</div>
                                    ) : (
                                        viewTask.comments.map((c, i) => (
                                            <div key={i} className={styles.commentItem}>
                                                {c}
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className={styles.commentInputRow}>
                                    <input
                                        type="text"
                                        placeholder="Write a comment and press Add..."
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault()
                                                addComment()
                                            }
                                        }}
                                    />
                                    <button className={styles.addCommentBtn} onClick={addComment}>
                                        <i className="material-symbols-rounded">send</i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Invite area (dummy) */}
                        <div className={styles.inviteArea}>
                            <div className={styles.inviteRow}>
                                <input
                                    placeholder="Invite people to this board..."
                                    value={inviteQueryView}
                                    onChange={(e) => setInviteQueryView(e.target.value)}
                                />
                                <button className={styles.inviteBtn} title="Invite (disabled)">
                                    Invite
                                </button>
                            </div>
                            <div className={styles.searchDropdown}>
                                <div className={styles.emptyState}>No users found</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Tasks
